import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api, { formatINR, formatINRShort } from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import VenueTabs from "@/pages/dashboard/VenueTabs";
import ProfitLoss from "@/pages/dashboard/ProfitLoss";
import { Skeleton } from "@/components/ui/skeleton";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function ProfitLossView() {
  const { venues, venuesLoading, monthDate, setMonthDate, businessId, user } = useDash();
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const changeMonth = (d) => setMonthDate(new Date(y, m + d, 1));
  const isEmployee = user?.role === "employee";

  const { data: analytics } = useQuery({
    queryKey: ["analytics", "pnl-trend", businessId],
    queryFn: async () => (await api.get("/analytics", { params: businessId ? { business_id: businessId } : {} })).data,
    enabled: !isEmployee && !!businessId,
  });

  if (venuesLoading) return <Skeleton className="h-96 w-full rounded-xl" />;

  return (
    <div className="space-y-6" data-testid="profit-loss-view">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Profit &amp; Loss</h1>
          <p className="text-sm text-slate">Track expenses per venue and see your real monthly profit.</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => changeMonth(-1)} data-testid="pnl-prev-month" className="grid h-9 w-9 place-items-center rounded-md border border-slate-200 text-slate transition-colors hover:bg-cloud"><ChevronLeft size={18} /></button>
          <span className="min-w-[130px] text-center text-sm font-semibold text-pulse-navy">{MONTHS[m]} {y}</span>
          <button onClick={() => changeMonth(1)} data-testid="pnl-next-month" className="grid h-9 w-9 place-items-center rounded-md border border-slate-200 text-slate transition-colors hover:bg-cloud"><ChevronRight size={18} /></button>
        </div>
      </div>

      {venues.length > 1 && <VenueTabs />}

      <ProfitLoss alwaysOpen />

      {!isEmployee && analytics?.trend && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm" data-testid="pnl-trend-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-pulse-navy">Profit trend · {analytics.fy_label}</h2>
            <span className="text-sm text-slate">Net profit this year <b className={analytics.total_profit >= 0 ? "text-pulse-green" : "text-red-600"}>{formatINR(analytics.total_profit)}</b></span>
          </div>
          <p className="mt-1 text-xs text-slate">Month-over-month profit (revenue minus recorded expenses) across the financial year.</p>
          <div className="mt-4 h-60 min-h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.trend} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={formatINRShort} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" name="Revenue" dataKey="revenue" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: "#10B981" }} activeDot={{ r: 5 }} />
                <Line type="monotone" name="Profit" dataKey="profit" stroke="#0A192F" strokeWidth={2.5} dot={{ r: 3, fill: "#0A192F" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
